import React, { useEffect, useRef } from 'react';
import workshopBackground from '../assets/images/51f93f4c-4adb-473b-982a-ee8247aa8dd8.jpg';
import luthierImage from '../assets/images/6363fc9c-5af2-4f7a-98b6-b04e535055ee.jpg';
import dorSignature from '../assets/images/sig.svg';

const AboutSection = () => {
  const parallaxRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current && sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        const scrolled = window.pageYOffset;
        const sectionTop = scrolled + rect.top;
        const windowHeight = window.innerHeight;
        
        // Apply parallax when section is in view - reduced rate on mobile
        if (rect.top < windowHeight && rect.bottom > 0) {
          const rate = (scrolled - sectionTop) * (window.innerWidth >= 768 ? 0.6 : 0.3);
          parallaxRef.current.style.transform = `translateY(${rate}px)`;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative py-32 overflow-hidden bg-black">
      {/* Background Parallax */}
      <div className="absolute inset-0 z-0 opacity-40">
        <img 
          ref={parallaxRef}
          src={workshopBackground} 
          alt="Guitar Workshop" 
          className="w-full h-[120%] object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-20">
          
          {/* Text Content */}
          <div className="lg:w-1/2 order-2 lg:order-1">
            <span className="text-theme-primary-400 tracking-[0.2em] uppercase text-sm font-bold mb-4 block">The Luthier</span>
            <h2 className="text-4xl md:text-6xl font-bold mb-8 text-white leading-tight">
              Crafted by <br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-theme-primary-300 to-theme-primary-600">Experience</span>
            </h2>
            
            <div className="space-y-8 text-gray-400 text-lg leading-relaxed font-light">
              <p className="border-l-2 border-theme-primary-500 pl-6 italic text-white/90">
                "Vitæ Guitars grew from a decade spent bringing old instruments back to life."
              </p>
              <p>
                After years of repairing, restoring, and learning the language of countless guitars, I turned that knowledge into building instruments of my own, ones that <span className="text-white font-medium">honor tradition while daring to push it forward.</span>
              </p>
              <p>
                Every guitar I create begins with carefully selected woods, hardware, and electronics, shaped with precision and intention. But more than the craft, it's about <span className="text-white font-medium">connection</span> - building instruments that feel inevitable in your hands.
              </p>
              <p>
                From reimagined classics to bold modern specs, each Vitæ guitar carries the lessons of every instrument I've worked on. These aren't just instruments; they're <span className="text-white font-medium">stories of sound, ready to become part of yours.</span>
              </p>
              
              <div className="pt-8">
                <img src={dorSignature} alt="Dor Biton Signature" className="h-28 md:h-32 opacity-90 invert" />
                <p className="text-sm text-gray-500 mt-2 tracking-widest uppercase">Dor Biton, Master Luthier</p>
              </div>
            </div>
          </div>

          {/* Image Content */}
          <div className="lg:w-1/2 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 border border-theme-primary-500/30 rounded-full scale-95 animate-pulse-slow"></div>
              <div className="relative rounded-full overflow-hidden w-80 h-80 md:w-[500px] md:h-[500px] mx-auto border border-white/10 shadow-2xl transition-all duration-700">
                <img
                  src={luthierImage}
                  alt="Dor Biton - Master Luthier"
                  className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                />
              </div>
              
              {/* Floating Badge Removed */}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;
